import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchHomeVisitClinicalRecord } from "../../../store/slices/homeVisitClinicalRecordSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function HomeVisitClinicalRecordPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentRecord, isLoading, error } = useSelector(
    (state) => state.homeVisitClinicalRecords
  );

  useEffect(() => {
    if (id) dispatch(fetchHomeVisitClinicalRecord(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentRecord) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(t);
        window.onafterprint = null;
      };
    }
  }, [currentRecord, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading Clinical Record print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentRecord) return null;

  const d = currentRecord;

  const fmt = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";

  return (
    <>
      <style jsx global>{`
        * {
          box-sizing: border-box !important;
          font-family: "Times New Roman", Times, serif !important;
        }

        body {
          background: #fff;
          color: #000;
          font-size: 10pt !important;
          line-height: 1.25 !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .print-container {
          padding: 5px !important;
          width: 100% !important;
          max-width: 800px;
          margin: 0 auto;
        }
        ${printFooterStyles}

        .header-logo {
          display: flex;
          justify-content: center;
          margin-bottom: 5px;
        }

        .header-logo img {
          height: 55px;
        }

        .header-text {
          text-align: center;
          margin-bottom: 12px;
        }

        .header-text h2 {
          font-size: 14pt !important;
          font-weight: bold !important;
          margin: 0 0 2px 0 !important;
          text-transform: uppercase;
        }

        .header-text h3 {
          font-size: 11pt !important;
          font-weight: bold !important;
          margin: 0 0 2px 0 !important;
        }

        .header-text h4 {
          font-size: 10pt !important;
          font-style: italic;
          margin: 0 !important;
        }

        .section-header {
          background-color: #eee;
          border: 1px solid #000;
          font-weight: bold;
          padding: 4px 8px;
          font-size: 10.5pt;
          margin-top: 10px;
          margin-bottom: 6px;
          text-transform: uppercase;
        }

        .info-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 8px;
        }

        .info-table td {
          border: 1px solid #000;
          padding: 3px 6px;
          font-size: 9.5pt;
          vertical-align: top;
        }

        .label {
          font-weight: bold;
          background-color: #f9f9f9;
        }

        .checkbox-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 4px;
        }

        .checkbox-item {
          display: flex;
          align-items: center;
          font-size: 9pt;
        }

        .checkbox {
          width: 12px;
          height: 12px;
          border: 1px solid #000;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-right: 6px;
          flex-shrink: 0;
          font-size: 9pt;
          font-weight: bold;
        }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
          <div className="header-logo"><img src={logo} alt="TRH Logo" /></div>
          <div className="header-text">
            <h2>The Recovery House (TRH)</h2>
            <h3>Home Visitation Program</h3>
            <h4>Home Visit Clinical Record Form</h4>
          </div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Client Name:</span>
            <span className="info-val-underline">
              {d.patient ? `${d.patient.first_name || ""} ${d.patient.last_name || ""}` : "-"}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">MR#:</span>
            <span className="info-val-underline">{d.patient?.mr_no || "-"}</span>
          </div>
        </div>

        <div className="grid-row-3">
          <div className="info-item">
            <span className="info-label">Visit Number:</span>
            <span className="info-val-underline">{d.visit_number || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Date of Visit:</span>
            <span className="info-val-underline">{fmt(d.date_of_visit)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Duration:</span>
            <span className="info-val-underline">{d.duration || "-"}</span>
          </div>
        </div>

        <div className="grid-row-3">
          <div className="info-item">
            <span className="info-label">Time In:</span>
            <span className="info-val-underline">{d.time_in || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Time Out:</span>
            <span className="info-val-underline">{d.time_out || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Type of Visit:</span>
            <span className="info-val-underline">{d.type_of_visit || "-"}</span>
          </div>
        </div>

        <div className="info-item">
          <span className="info-label">Location Address:</span>
          <span className="info-val-underline">{d.location_address || "-"}</span>
        </div>

        {/* 2. TRH Visiting Team */}
        <div className="section-title">2. TRH Visiting Team</div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Psychiatrist:</span>
            <span className="info-val-underline">{d.psychiatrist_name || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Rehab Practitioner:</span>
            <span className="info-val-underline">{d.rehab_practitioner_name || "-"}</span>
          </div>
        </div>

        <div className="grid-row-3">
          <div className="info-item">
            <span className="info-label">Rehab Assistant:</span>
            <span className="info-val-underline">{d.rehab_assistant_name || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Driver:</span>
            <span className="info-val-underline">{d.driver_name || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Team Gender:</span>
            <span className="info-val-underline">{d.team_gender || "-"}</span>
          </div>
        </div>

        <div className="grid-row-3">
          <div className="info-item">
            <span className="info-label">Family Member Present:</span>
            <span className="info-val-underline">{d.family_member_name || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Relation:</span>
            <span className="info-val-underline">{d.family_member_relation || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Contact Number:</span>
            <span className="info-val-underline">{d.family_member_contact || "-"}</span>
          </div>
        </div>

        {/* 3. Safety & Environment Check */}
        <div className="section-title">3. Safety & Environment Check</div>
        <div className="checkbox-container">
          <div className="checkbox-item">
            <span className="checkbox-box">{d.safety_environment_safe ? "X" : ""}</span>
            <span>Environment safe for team</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.safety_no_weapons ? "X" : ""}</span>
            <span>No visible weapons</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.safety_no_intoxicated ? "X" : ""}</span>
            <span>No intoxicated individuals</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.safety_client_stable ? "X" : ""}</span>
            <span>Client medically stable</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.safety_family_cooperative ? "X" : ""}</span>
            <span>Family cooperative</span>
          </div>
        </div>
        {d.safety_unsafe_action_description && (
          <div className="info-item mt-1">
            <span className="info-label">Unsafe Action description:</span>
            <span className="info-val-underline">{d.safety_unsafe_action_description}</span>
          </div>
        )}

        {/* 4. Current Clinical Status */}
        <div className="section-title">4. Current Clinical Status (Mental State Overview)</div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Appearance/Hygiene:</span>
            <span className="info-val-underline">{d.clinical_appearance_hygiene || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Behavior:</span>
            <span className="info-val-underline">{d.clinical_behavior || "-"}</span>
          </div>
        </div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Speech:</span>
            <span className="info-val-underline">{d.clinical_speech || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Mood/Affect:</span>
            <span className="info-val-underline">{d.clinical_mood_affect || "-"}</span>
          </div>
        </div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Thought Content:</span>
            <span className="info-val-underline">{d.clinical_thought_content || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Perception:</span>
            <span className="info-val-underline">{d.clinical_perception || "-"}</span>
          </div>
        </div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Insight:</span>
            <span className="info-val-underline">{d.clinical_insight || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Judgment:</span>
            <span className="info-val-underline">{d.clinical_judgment || "-"}</span>
          </div>
        </div>
        <div className="checkbox-container">
          <div className="checkbox-item">
            <span className="checkbox-box">{d.clinical_suicidal_ideation ? "X" : ""}</span>
            <span>Suicidal Ideation</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.clinical_homicidal_ideation ? "X" : ""}</span>
            <span>Homicidal Ideation</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.clinical_impulsivity ? "X" : ""}</span>
            <span>Impulsivity</span>
          </div>
          <div className="checkbox-item">
            <span className="info-label">Risk Level:</span>
            <span className="info-val-underline">{d.clinical_risk_level || "-"}</span>
          </div>
        </div>
        {d.clinical_risk_action_taken && (
          <div className="info-item">
            <span className="info-label">Action Taken:</span>
            <span className="info-val-underline">{d.clinical_risk_action_taken}</span>
          </div>
        )}

        {/* 5. Medication Review */}
        <div className="section-title">5. Medication Review</div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Currently on Meds:</span>
            <span className="info-val-underline">{d.med_currently_on ? "Yes" : "No"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Adherence Compliance:</span>
            <span className="info-val-underline">{d.med_adherence || "-"}</span>
          </div>
        </div>
        <div className="info-item">
          <span className="info-label">Side Effects:</span>
          <span className="info-val-underline">{d.med_side_effects || "-"}</span>
        </div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Changes:</span>
            <span className="info-val-underline">{d.med_changes || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Prescribed By:</span>
            <span className="info-val-underline">{d.med_prescribed_by || "-"}</span>
          </div>
        </div>
        <div className="info-item">
          <span className="info-label">Change Details:</span>
          <span className="info-val-underline">{d.med_changes_details || "-"}</span>
        </div>

        {/* 6. Family Psychoeducation */}
        <div className="section-title">6. Family Psychoeducation (30 mins SOP)</div>
        <div className="checkbox-container">
          <div className="checkbox-item">
            <span className="checkbox-box">{d.psychoedu_illness_understanding ? "X" : ""}</span>
            <span>Illness understanding</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.psychoedu_medication_importance ? "X" : ""}</span>
            <span>Medication importance</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.psychoedu_relapse_signs ? "X" : ""}</span>
            <span>Relapse signs</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.psychoedu_behavior_management ? "X" : ""}</span>
            <span>Behavior management</span>
          </div>
        </div>
        <div className="checkbox-container">
          <div className="checkbox-item">
            <span className="checkbox-box">{d.psychoedu_communication_strategies ? "X" : ""}</span>
            <span>Communication strategies</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.psychoedu_crisis_handling ? "X" : ""}</span>
            <span>Crisis handling</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.psychoedu_rehab_readiness ? "X" : ""}</span>
            <span>Rehab readiness</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.psychoedu_residential_vs_drp ? "X" : ""}</span>
            <span>Residential vs DRP</span>
          </div>
        </div>
        <div className="grid-row-2 mt-1">
          <div className="info-item">
            <span className="info-label">Response:</span>
            <span className="info-val-underline">{d.psychoedu_family_response || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Notes:</span>
            <span className="info-val-underline">{d.psychoedu_notes || "-"}</span>
          </div>
        </div>

        {/* 7. Client Engagement */}
        <div className="section-title">7. Client Engagement Session</div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Participation Level:</span>
            <span className="info-val-underline">{d.engagement_participation || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Motivation Level:</span>
            <span className="info-val-underline">{d.engagement_motivation || "-"}</span>
          </div>
        </div>
        <div className="info-item">
          <span className="info-label">Issues Discussed:</span>
          <span className="info-val-underline">{d.engagement_issues_discussed || "-"}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Coping Style:</span>
          <span className="info-val-underline">{d.engagement_coping_style || "-"}</span>
        </div>

        {/* 8. Goal Setting */}
        <div className="section-title">8. Goal Setting / Rehab Readiness</div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Short-term Goal 1:</span>
            <span className="info-val-underline">{d.goals_short_term_1 || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Short-term Goal 2:</span>
            <span className="info-val-underline">{d.goals_short_term_2 || "-"}</span>
          </div>
        </div>
        <div className="info-item">
          <span className="info-label">Suitability Decision:</span>
          <span className="info-val-underline">{d.goals_suitability || "-"}</span>
        </div>

        {/* 9. Plan & 10. Incidents */}
        <div className="grid-row-2">
          <div>
            <div className="section-title">9. Plan After Visit</div>
            <div className="info-item">
              <span className="info-label">Decision:</span>
              <span className="info-val-underline">{d.plan_decision || "-"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Next Date:</span>
              <span className="info-val-underline">{fmt(d.plan_next_visit_date)}</span>
            </div>
          </div>
          <div>
            <div className="section-title">10. Incident Documentation</div>
            <div className="info-item">
              <span className="info-label">Aggressive/Disruptive:</span>
              <span className="info-val-underline">{d.incident_aggression_disruption ? "Yes" : "No"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Description:</span>
              <span className="info-val-underline">{d.incident_description || "-"}</span>
            </div>
          </div>
        </div>

        {/* 11. Clinical Impression & 13. Audits */}
        <div className="section-title">11. Clinical Impression & Audit Tracking</div>
        <div className="grid-row-2">
          <div className="info-item">
            <span className="info-label">Summary of Visit:</span>
            <span className="info-val-underline">{d.impression_summary || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Progress:</span>
            <span className="info-val-underline">{d.impression_progress || "-"}</span>
          </div>
        </div>
        <div className="checkbox-container">
          <div className="checkbox-item">
            <span className="checkbox-box">{d.audit_completed_within_1hour ? "X" : ""}</span>
            <span>Completed within 1-hr standard</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.audit_documentation_same_day ? "X" : ""}</span>
            <span>Documentation completed same day</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.audit_medication_chart_updated ? "X" : ""}</span>
            <span>Medication chart updated</span>
          </div>
          <div className="checkbox-item">
            <span className="checkbox-box">{d.audit_risk_reassessment_completed ? "X" : ""}</span>
            <span>Risk reassessment completed</span>
          </div>
        </div>

        {/* 12. Finance Information */}
        <div className="section-title">12. Finance Information</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "10px", marginBottom: "15px" }}>
          <div className="info-item">
            <span className="info-label" style={{ fontWeight: "bold" }}>Amount:</span>
            <span className="info-val-underline">{d.amount || "—"}</span>
          </div>
          <div className="info-item">
            <span className="info-label" style={{ fontWeight: "bold" }}>Payment Status:</span>
            <span className="info-val-underline">{d.payment_status || "—"}</span>
          </div>
          <div className="info-item">
            <span className="info-label" style={{ fontWeight: "bold" }}>Reference No:</span>
            <span className="info-val-underline">{d.reference_no || "—"}</span>
          </div>
          <div className="info-item">
            <span className="info-label" style={{ fontWeight: "bold" }}>Payment Date:</span>
            <span className="info-val-underline">{fmt(d.payment_date) || "—"}</span>
          </div>
        </div>

        {/* 13. Signatures */}
        <div className="signature-block">
          <div className="signature-item">
            <div className="signature-line">Psychiatrist Signature / Date</div>
            <div style={{ fontSize: "8.5pt" }}>
              {d.signature_psychiatrist ? `${d.signature_psychiatrist} (${fmt(d.signature_psychiatrist_date)})` : ""}
            </div>
          </div>
          <div className="signature-item">
            <div className="signature-line">Rehab Practitioner Signature</div>
            <div style={{ fontSize: "8.5pt" }}>
              {d.signature_rehab_practitioner || ""}
            </div>
          </div>
          <div className="signature-item">
            <div className="signature-line">Family Member Signature</div>
            <div style={{ fontSize: "8.5pt" }}>
              {d.signature_family_member || ""}
            </div>
          </div>
        </div>

      </div>
      </PrintWrapper>
    </>
  );
}
